import { useQuery } from "@tanstack/react-query";
import type { Zone } from "./useZoneSearch";

/**
 * Maps Zone types to Nominatim featuretype parameter for more accurate results.
 * Nominatim featuretype: country | state | city | settlement
 */
const ZONE_TYPE_TO_FEATURETYPE: Record<string, string | undefined> = {
  region: "state",
  city: "city",
  municipality: "city",
};

/**
 * English→Italian translations for common tourist zone names in the DB.
 * Nominatim works better with Italian names.
 */
const NAME_TRANSLATIONS: Record<string, string> = {
  "lake garda": "Lago di Garda",
  "lake como": "Lago di Como",
  "lake maggiore": "Lago Maggiore",
  "lake trasimeno": "Lago Trasimeno",
  "iseo lake": "Lago d'Iseo",
  "molveno lake": "Lago di Molveno",
  "elba island": "Isola d'Elba",
  "aeolian islands": "Isole Eolie",
  "aegadian islands": "Isole Egadi",
  "tremiti islands": "Isole Tremiti",
  "lampedusa island": "Lampedusa",
  "amalfi coast": "Costiera Amalfitana",
  "sorrento coast": "Penisola Sorrentina",
  "cinque terre": "Cinque Terre",
  "etna area": "Etna",
  "chianti area": "Chianti",
};

/**
 * Cleans zone name for Nominatim lookup.
 * DB stores cities as "PONTECAGNANO FAIANO - SALERNO" (city - province).
 * Nominatim needs just "Pontecagnano Faiano" to find the admin boundary.
 */
function cleanZoneName(name: string, type: string): string {
  let cleaned = name;

  // Strip " - PROVINCE" suffix for cities/municipalities (not regions)
  if (type !== "region") {
    cleaned = cleaned.replace(/\s*[-–]\s*[A-Z][A-Z\s'']+$/, "");
  }

  // Strip leading postal code + trailing province code: "00040 Ardea RM" → "Ardea"
  cleaned = cleaned.replace(/^\d{5}\s+/, "").replace(/\s+[A-Z]{2}$/, "");

  // Check for known English→Italian translations
  const translated = NAME_TRANSLATIONS[cleaned.toLowerCase()];
  if (translated) return translated;

  // Title-case: "PONTECAGNANO FAIANO" → "Pontecagnano Faiano"
  cleaned = cleaned
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return cleaned;
}

/**
 * Fetches the administrative boundary polygon for a given zone (city/region)
 * from the Nominatim / OpenStreetMap API.
 *
 * Returns a GeoJSON Geometry (Polygon or MultiPolygon) that can be rendered
 * on the Leaflet map as a highlighted area.
 */
export function useZoneBoundary(zone: Zone | null) {
  return useQuery<GeoJSON.Geometry | null>({
    queryKey: ["zone-boundary", zone?.name ?? "", zone?.type ?? ""],
    queryFn: async () => {
      if (!zone) return null;

      const searchName = cleanZoneName(zone.name, zone.type);
      // If the name was translated, it's likely a tourist area — don't restrict featuretype
      const isTranslated = !!NAME_TRANSLATIONS[zone.name.toLowerCase()] ||
        !!NAME_TRANSLATIONS[zone.name.replace(/\s*[-–]\s*[A-Z][A-Z\s'']+$/, "").toLowerCase()];

      const params = new URLSearchParams({
        q: `${searchName}, Italia`,
        format: "json",
        polygon_geojson: "1",
        limit: "5",
        countrycodes: "it",
      });

      // Use featuretype to narrow results, but skip for tourist areas
      if (!isTranslated) {
        const featuretype = ZONE_TYPE_TO_FEATURETYPE[zone.type];
        if (featuretype) {
          params.set("featuretype", featuretype);
        }
      }

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params.toString()}`,
        {
          headers: {
            "Accept-Language": "it",
          },
        }
      );

      if (!response.ok) {
        console.warn(`[useZoneBoundary] Nominatim request failed: ${response.status}`);
        return null;
      }

      const results = await response.json();

      if (!results || results.length === 0) {
        console.warn(`[useZoneBoundary] No results for "${searchName}"`);
        return null;
      }

      // Find the first result that has a valid Polygon or MultiPolygon geojson.
      // Nodes (points) don't have polygon geometry even with polygon_geojson=1.
      // Prefer relations (osm_type "R") which are administrative boundaries.
      for (const result of results) {
        const geojson = result.geojson;
        if (geojson && (geojson.type === "Polygon" || geojson.type === "MultiPolygon")) {
          return geojson as GeoJSON.Geometry;
        }
      }

      console.warn(`[useZoneBoundary] No polygon geometry found in ${results.length} results for "${searchName}"`);
      return null;
    },
    enabled: !!zone,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours — boundaries don't change
    gcTime: 24 * 60 * 60 * 1000,
  });
}
